/**
 * Component that displays key performance indicators for equipment.
 * 
 * This component initializes KPI data when the page loads using site and apmId parameters.
 * KPI data remains static regardless of tree node selection.
 */

import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import { KPIData } from '../../../services/equipment/types';
import { useEquipmentContext } from '../../../contexts/equipment-context';

interface KPIPanelProps {
  site: string;
  apmId: string;
}

export const KPIPanel: React.FC<KPIPanelProps> = ({ site, apmId }) => {
  const { state } = useEquipmentContext();  
  
  // Show loading state
  if (state.isLoading.kpi) {
    return (
      <Column spacing="400" alignmentHorizontal="center">
        <Loader size="medium" />
        <Text>Loading KPI data...</Text>
      </Column>
    );
  }
  
  // Show error state with retry functionality
  if (state.errors.kpi || !state.kpiData) {
    return (
      <Column spacing="400">
        <Alert type="error" title="Error loading KPI data">
            <Text>{state.errors.kpi || "Error loading the KPI data."}</Text>
        </Alert>
      </Column>
    );
  }

  const displayData = state.kpiData;

  return (
    <Column spacing="400">
      <Row spacing="300">
        <Box width="fill" spacingInset="500" backgroundColor="subdued">
          <Column spacing="200" alignmentHorizontal="center">
            <Text type="h300">BRKD</Text>
            <Text type="h200" color={displayData.brkd > 0 ? "error" : undefined}>
              {displayData.brkd}
            </Text>
          </Column>
        </Box>
        
        <Box width="fill" spacingInset="500" backgroundColor="subdued">
          <Column spacing="200" alignmentHorizontal="center">
            <Text type="h300">PM</Text>
            <Text type="h200">
              {displayData.pm}
            </Text>
          </Column>
        </Box>
        
        <Box width="fill" spacingInset="500" backgroundColor="subdued">
          <Column spacing="200" alignmentHorizontal="center">
            <Text type="h300">Last PM</Text>
            <Row spacing="100" alignmentVertical="center" alignmentHorizontal="center">
              <Text type="h200" color={displayData.daysSinceLastPM < 60 ? "error" : undefined}>
                {displayData.daysSinceLastPM}
              </Text>
              {displayData.daysSinceLastPM < 60 && (
                <Text type="h200" color="error">⚠</Text>
              )}
            </Row>
          </Column>
        </Box>
      </Row>
    </Column>
  );
};
